
import { Vue, Component, ModelSync, Prop } from "vue-property-decorator";
import AlignGridIndicator from "@/oxygen/classes/inputs/align/AlignGridIndicator.vue";
import { AlignInputValue } from "@/oxygen/classes/lib/align";

type HoverValue = {
  primary_axis: string | null;
  cross_axis: string | null;
};

@Component({
  components: { AlignGridIndicator }
})
export default class AlignGridInput extends Vue {
  @ModelSync("value", "input", {
    default: () => ({
      primary_axis: null,
      cross_axis: null
    })
  })
  syncedValue!: AlignInputValue;

  @Prop()
  placeholder!: AlignInputValue;

  @Prop()
  display!: string;

  @Prop()
  direction!: string;

  shiftKey = false;
  altKey = false;

  columnOptions = [
    ["start", "start"],
    ["center", "start"],
    ["end", "start"],
    ["start", "center"],
    ["center", "center"],
    ["end", "center"],
    ["start", "end"],
    ["center", "end"],
    ["end", "end"]
  ];

  rowOptions = [
    ["start", "start"],
    ["start", "center"],
    ["start", "end"],
    ["center", "start"],
    ["center", "center"],
    ["center", "end"],
    ["end", "start"],
    ["end", "center"],
    ["end", "end"]
  ];

  hoveredValue: HoverValue = {
    primary_axis: null,
    cross_axis: null
  };

  get internalValue() {
    return this.placeholder ? this.placeholder : this.syncedValue;
  }

  get invert() {
    return ["row", "wrap"].includes(this.direction);
  }

  get gridOptions() {
    return this.invert ? this.columnOptions : this.rowOptions;
  }

  get showHoverIndicator() {
    const { primary_axis, cross_axis } = this.internalValue;
    const { primary_axis: hoveredPrimaryAxis, cross_axis: hoveredCrossAxis } =
      this.hoveredValue;

    if (
      primary_axis === this.hoverIndicatorX &&
      "stretch" === this.hoverIndicatorY
    ) {
      return true;
    }

    if (primary_axis === "space-between" && cross_axis === "stretch") {
      return false;
    }

    const isHovered = hoveredPrimaryAxis && hoveredCrossAxis;
    const isDifferentAxis =
      this.hoverIndicatorX !== primary_axis || hoveredCrossAxis !== cross_axis;

    const isNotSpaceBetween = !(
      this.isDistributionAlignment(primary_axis) &&
      hoveredCrossAxis === cross_axis
    );

    return isHovered && isDifferentAxis && isNotSpaceBetween;
  }

  get hoverIndicatorX() {
    if (this.display === "flex") {
      if (
        this.shiftKey ||
        this.internalValue.primary_axis === "space-between"
      ) {
        return "space-between";
      }
    }

    return this.hoveredValue.primary_axis;
  }

  get hoverIndicatorY() {
    if (this.altKey || this.internalValue.cross_axis === "stretch") {
      return "stretch";
    }

    return this.hoveredValue.cross_axis;
  }

  isDistributionAlignment(value: string) {
    return ["space-between", "space-around"].includes(value);
  }

  setValue(primaryAxis: string, crossAxis: string) {
    if (this.shiftKey && this.display === "flex") {
      this.syncedValue.primary_axis = "space-between";
    } else if (!this.isDistributionAlignment(this.internalValue.primary_axis)) {
      this.syncedValue.primary_axis = primaryAxis;
    }

    if (this.altKey) {
      this.syncedValue.cross_axis = "stretch";
    } else if (this.internalValue.cross_axis !== "stretch") {
      this.syncedValue.cross_axis = crossAxis;
    }

    // TODO: Why does only reassigning the object trigger the reactivity?
    this.syncedValue = { ...this.syncedValue };
  }

  onDoubleClick(primaryAxis: string, crossAxis: string) {
    const toggleProperty =
      this.display === "flex" ? "space-between" : "stretch";

    // This toggles the primary axis between space-between and the selected value
    if (this.internalValue.primary_axis === toggleProperty) {
      this.syncedValue.primary_axis = primaryAxis;
    } else {
      this.internalValue.primary_axis = toggleProperty;
    }

    // We don't permit stretch on the cross axis when space-between is selected
    if (this.internalValue.cross_axis === "stretch") {
      this.syncedValue.cross_axis = crossAxis;
    }

    // TODO: Why does only reassigning the object trigger the reactivity?
    this.syncedValue = { ...this.syncedValue };
  }

  onKeyDown(event: KeyboardEvent) {
    this.shiftKey = event.shiftKey;
    this.altKey = event.altKey;
  }

  onDotEnter(primaryAxis: string, crossAxis: string) {
    this.hoveredValue = {
      primary_axis: primaryAxis,
      cross_axis: crossAxis
    };
  }

  onDotLeave() {
    this.hoveredValue = {
      primary_axis: null,
      cross_axis: null
    };
  }

  resetKeysOnTabOut() {
    // Reset shift key when the tab is switched or the browser window
    // is minimized to prevent the shift key from being stuck
    this.shiftKey = false;
    this.altKey = false;
  }

  isDotVisible(dotX: string, dotY: string) {
    const { primary_axis, cross_axis } = this.internalValue;
    const hPrimaryAxis = this.hoveredValue.primary_axis;
    const hCrossAxis = this.hoveredValue.cross_axis;

    if (primary_axis === "space-between" && cross_axis === "stretch") {
      return false;
    }

    if (
      (primary_axis === "space-between" && dotY === cross_axis) ||
      (this.hoverIndicatorX === "space-between" && dotY === hCrossAxis)
    ) {
      return false;
    }

    if (dotX === primary_axis && dotY === cross_axis) {
      return false;
    }

    if (dotX === hPrimaryAxis && dotY === hCrossAxis) {
      return false;
    }

    return true;
  }

  mounted() {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyDown);
    document.addEventListener("visibilitychange", this.resetKeysOnTabOut);
  }

  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyDown);
    document.removeEventListener("visibilitychange", this.resetKeysOnTabOut);
  }
}
